import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { ModificationRequestDeliverableForDetailFragment } from '../../../../generated/graphql';
import { toJSDate } from '../../../../graphql/datetime';
import theme from '../../../../theme';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import _FileSize from '../../primitive/FileSize';
import ImageScreen from '../../primitive/ImageScreen';
import LfToBr from '../../primitive/LfToBr';
import MediaPlayer from '../MediaPlayer';

interface Props {
  deliverable: ModificationRequestDeliverableForDetailFragment;
  className?: string;
}

const Item: FC<Props> = ({ deliverable, className }) => {
  return (
    <Root className={className}>
      {deliverable ? (
        <DeliverableScreen>
          <VideoOrImage aspectRatio={[4, 3]}>
            <MediaPlayer mimeType={deliverable.creative.file.mimeType} url={deliverable.creative.file.url!} />
          </VideoOrImage>
        </DeliverableScreen>
      ) : null}

      {deliverable.attachedThumbnail && (
        <Attachment>
          <AttachmentHeading variant="subtitle1">
            <FormattedMessage id="deliverable_detail.heading.attachment" />
          </AttachmentHeading>
          <AttachedThumbnail src={deliverable.attachedThumbnail.file.url!} clickable />
          <AttatchmentType>
            <FormattedMessage id="deliverable_detail.attachment_type.thumbnail_image" />
          </AttatchmentType>
          <FileSize fileSize={deliverable.attachedThumbnail.file.size}>
            {(fileSize) => <Typography variant="caption">{fileSize}</Typography>}
          </FileSize>
        </Attachment>
      )}

      <Content>
        <Titles>
          <Title variant="subtitle1">
            <FormattedDate
              value={toJSDate(deliverable.createdAt)}
              year="numeric"
              month="2-digit"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
            />
          </Title>
          <Title variant="subtitle1">
            <FormattedMessage id="show_creation_page.modification_request_deliverable" />
          </Title>
        </Titles>
        <IntentHeading variant="subtitle2" noWrap>
          <FormattedMessage id="deliverable.intent_heading_modification" />
        </IntentHeading>
        <Intent variant="body2">
          <LfToBr text={deliverable.intent} />
        </Intent>
      </Content>
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['35%', 'minmax(0, 680px)'],
    rows: ['auto'],
    areas: [
      ['deliverable', 'content'],
      ['attachment', 'content'],
    ],
  }),
  {
    padding: SPACING_REGULAR,
    borderTop: `1px ${theme.palette.grey['200']} solid`,
  },
);

const DeliverableScreen = styled('div')(gridLayoutItem('deliverable', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
});

const VideoOrImage = styled(AspectRatioFixed)({
  marginBottom: SPACING_REGULAR,
});

const Content = styled('div')(gridLayoutItem('content', { alignSelf: 'flex-start' }), {
  paddingLeft: SPACING_REGULAR,
});

const Titles = styled('div')({
  marginBottom: SPACING_REGULAR,
});

const Title = styled(Typography)({});

const Attachment = styled('div')(
  gridLayoutContainer({
    columns: ['auto', '1fr'],
    rows: ['auto', '60px', '60px'],
    areas: [
      ['attachmentHeading', 'attachmentHeading'],
      ['attachedThumbnail', 'attachmentType'],
      ['attachedThumbnail', 'attachmentFileSize'],
    ],
  }),
  gridLayoutItem('attachment'),
  {
    marginTop: SPACING_UNIT,
    alignContent: 'flex-start',
  },
  {
    [mq('sp')]: {
      margin: `0 0 ${SPACING_LARGE}px`,
    },
  },
);

const AttachedThumbnail = styled(ImageScreen)(gridLayoutItem('attachedThumbnail', { alignSelf: 'center' }), {
  marginRight: SPACING_REGULAR,
  maxWidth: 200,
  height: 120,
});

const AttatchmentType = styled(Typography)(gridLayoutItem('attachmentType', { alignSelf: 'end' }), {
  whiteSpace: 'nowrap',
});
AttatchmentType.defaultProps = { color: 'textSecondary' };

const FileSize = styled(_FileSize)(gridLayoutItem('attachmentFileSize'));

const AttachmentHeading = styled(Typography)(gridLayoutItem('attachmentHeading'), {
  marginBottom: SPACING_REGULAR,
});

const IntentHeading = styled(Typography)(gridLayoutItem('intentHeading'), { marginBottom: SPACING_UNIT });

const Intent = styled(Typography)(gridLayoutItem('intentBody'), {
  wordBreak: 'break-word' as any,
  hyphens: 'auto',
  lineHeight: '1.6',
});

export default Item;
